/**
 * Keyboard Shortcut Management Service
 * Centralized keyboard shortcut handling and custom shortcut storage
 */

import { doc, getDoc, setDoc, Timestamp, FieldValue } from 'firebase/firestore';
import { db } from '../config/firebase';
import { createComponentLogger } from '../utils/logger';

const logger = createComponentLogger('KeyboardShortcutService');

export interface Shortcut {
  id: string;
  key: string;
  description: string;
  context?: string;
  isSystem: boolean;
  canCustomize: boolean;
  /**
   * S132: whether any component actually calls `registerShortcut` for this id.
   *
   * The catalogue was written as a wish list — sixteen chords, six of them with
   * handlers — and both readers presented all sixteen as working: the shortcuts
   * dialog listed them, and `ShortcutSettings` offered to REBIND them, so a
   * child could carefully assign a new key to a command that has never existed.
   *
   * `keyboardShortcutService.test.ts` asserts this flag against a grep for
   * `registerShortcut('<id>'` across src/, so it cannot drift from the truth in
   * either direction: marking something implemented without wiring it fails,
   * and wiring something without marking it fails too.
   */
  implemented: boolean;
}

export interface CustomShortcut {
  shortcutId: string;
  key: string;
  customKey?: string;
  isCustom: boolean;
  updatedAt?: Timestamp | FieldValue;
}

// System shortcuts definition
export const SHORTCUTS: Shortcut[] = [
  // File Operations
  {
    id: 'save-project',
    key: 'Ctrl+S',
    description: 'Save project',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: true
  },
  {
    id: 'run-project',
    key: 'F5',
    description: 'Run/Execute project',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: false
  },
  {
    id: 'fullscreen-preview',
    key: 'F11',
    description: 'Fullscreen preview',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: false
  },

  // Edit Operations
  {
    id: 'undo',
    key: 'Ctrl+Z',
    description: 'Undo',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: false
  },
  {
    id: 'redo',
    key: 'Ctrl+Shift+Z',
    description: 'Redo',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: false
  },
  {
    id: 'find',
    key: 'Ctrl+F',
    description: 'Find in code',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: false
  },
  {
    id: 'toggle-comment',
    key: 'Ctrl+/',
    description: 'Toggle code comments',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: false
  },

  // Navigation
  {
    id: 'quick-open',
    key: 'Ctrl+P',
    description: 'Quick open/command palette',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: false
  },
  {
    id: 'toggle-sidebar',
    key: 'Ctrl+B',
    description: 'Toggle sidebar',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: false
  },
  {
    id: 'switch-panel-chat',
    key: 'Ctrl+1',
    description: 'Switch to Chat panel',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: true
  },
  {
    id: 'switch-panel-code',
    key: 'Ctrl+2',
    description: 'Switch to Code panel',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: true
  },
  {
    id: 'switch-panel-preview',
    key: 'Ctrl+3',
    description: 'Switch to Preview panel',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: true
  },

  // Tab Navigation
  {
    id: 'tab-html',
    key: 'Alt+1',
    description: 'Switch to HTML tab',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: true
  },
  {
    id: 'tab-css',
    key: 'Alt+2',
    description: 'Switch to CSS tab',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: true
  },
  {
    id: 'tab-js',
    key: 'Alt+3',
    description: 'Switch to JavaScript tab',
    context: 'editor',
    isSystem: true,
    canCustomize: true,
    implemented: true
  },

  // Messaging
  {
    id: 'send-message',
    key: 'Ctrl+Enter',
    description: 'Run code / Send message',
    context: 'all',
    isSystem: true,
    canCustomize: true,
    implemented: true
  },

  // Modal/Dialog
  {
    id: 'close-modal',
    key: 'Escape',
    description: 'Close modal/panel',
    context: 'all',
    isSystem: true,
    canCustomize: false,
    implemented: true
  }
];

// Shortcut handlers
const shortcutHandlers = new Map<string, () => void>();
const shortcutsByContext = new Map<string, Shortcut[]>();

// Initialize shortcuts by context
SHORTCUTS.forEach(shortcut => {
  const context = shortcut.context || 'all';
  if (!shortcutsByContext.has(context)) {
    shortcutsByContext.set(context, []);
  }
  shortcutsByContext.get(context)!.push(shortcut);
});

/**
 * Register a keyboard shortcut handler
 */
/**
 * UI-shaped view of the canonical SHORTCUTS list.
 *
 * #58 fix (2026-05-18): ShortcutSettings.tsx previously declared its own
 * parallel array (`s1`-`s20` IDs, `group`, `keys[]` shape). That array
 * could drift from the canonical list without anyone noticing. This export
 * derives the UI shape from the source of truth, so adding a shortcut
 * happens in one place (the SHORTCUTS array above).
 *
 * Mapping:
 *   service `context`           → UI `group`
 *     'editor'                  → 'editor'
 *     'navigation' / undefined  → 'general' | 'navigation'
 *     'preview'                 → 'preview'
 *   service `key: 'Ctrl+S'`     → UI `keys: ['Ctrl', 'S']`
 */
export type UIShortcutGroup = 'general' | 'editor' | 'navigation' | 'preview';
export interface UIShortcut {
  id: string;
  group: UIShortcutGroup;
  action: string;
  keys: string[];
  description: string;
  canCustomize: boolean;
  /** S132: see {@link Shortcut.implemented}. Surfaced so the customize screen
   *  cannot silently offer to rebind a chord that has no handler. */
  implemented: boolean;
}

const inferGroup = (context?: string, id?: string): UIShortcutGroup => {
  if (context === 'editor') return 'editor';
  if (context === 'preview') return 'preview';
  if (context === 'navigation') return 'navigation';
  // Fall-back heuristic for shortcuts without explicit context.
  if (id?.startsWith('nav-') || id?.startsWith('go-')) return 'navigation';
  if (id?.startsWith('preview-') || id?.startsWith('run-')) return 'preview';
  if (id?.startsWith('edit-') || id?.startsWith('save-')) return 'editor';
  return 'general';
};

const splitKey = (k: string): string[] => k.split('+').map((s) => s.trim());

export const UI_SHORTCUTS: UIShortcut[] = SHORTCUTS.map((s) => ({
  id: s.id,
  group: inferGroup(s.context, s.id),
  // Action is a Title-Case rendering of the description; UI was previously
  // duplicating descriptions as both `action` and `description`.
  action: s.description,
  keys: splitKey(s.key),
  description: s.description,
  canCustomize: s.canCustomize,
  implemented: s.implemented,
}));

export const registerShortcut = (
  key: string,
  action: () => void,
  context?: string
): void => {
  try {
    const handlerKey = `${context || 'all'}:${key}`;
    shortcutHandlers.set(handlerKey, action);
    logger.debug(`Registered shortcut: ${key} in context ${context || 'all'}`);
  } catch (error) {
    logger.error('Error registering shortcut:', error);
  }
};

/**
 * Unregister a keyboard shortcut
 */
export const unregisterShortcut = (key: string, context?: string): void => {
  try {
    const handlerKey = `${context || 'all'}:${key}`;
    shortcutHandlers.delete(handlerKey);
    logger.debug(`Unregistered shortcut: ${key}`);
  } catch (error) {
    logger.error('Error unregistering shortcut:', error);
  }
};

/**
 * Get shortcuts available in a context
 */
export const getShortcutsForContext = (context: string): Shortcut[] => {
  const contextShortcuts = shortcutsByContext.get(context) || [];
  const allShortcuts = shortcutsByContext.get('all') || [];

  return [...contextShortcuts, ...allShortcuts].filter(
    (shortcut, index, self) =>
      index === self.findIndex(s => s.id === shortcut.id)
  );
};

/**
 * Get all available shortcuts
 */
export const getAllShortcuts = (): Shortcut[] => {
  return [...SHORTCUTS];
};

/**
 * Check if a keyboard event matches a shortcut string
 */
export const isShortcutPressed = (event: KeyboardEvent, shortcutString: string): boolean => {
  try {
    // Parse shortcut string (e.g., "Ctrl+S", "Shift+Alt+D")
    const parts = shortcutString.split('+').map(p => p.trim().toLowerCase());

    // Get the actual key
    const keyPart = parts.find(p => !['ctrl', 'shift', 'alt', 'cmd', 'meta'].includes(p));
    if (!keyPart) return false;

    const eventKey = event.key.toLowerCase();
    const keyMatch = eventKey === keyPart || event.code.toLowerCase() === `key${keyPart}`;

    // #29 fix (2026-05-17): the previous Mac branch required `!parts.includes('ctrl')`,
    // which meant a Mac shortcut def with `'ctrl+s'` could never trigger even
    // though Mac users do sometimes use Control (and Karabiner remaps make
    // it common). Use `navigator.userAgentData.platform` when available (more
    // reliable than the deprecated `navigator.platform`), fall back to the
    // legacy field. On Mac, treat the shortcut def's `cmd`/`meta` and `ctrl`
    // as interchangeable primary modifiers so a single def works cross-OS.
    const platform =
      (navigator as Navigator & { userAgentData?: { platform?: string } })
        .userAgentData?.platform || navigator.platform || '';
    const isMac = /Mac|iPhone|iPad|iPod/i.test(platform);

    const primaryRequested = parts.includes('cmd') || parts.includes('meta') || parts.includes('ctrl');
    const primaryPressed = isMac ? event.metaKey || event.ctrlKey : event.ctrlKey;

    const modifiersMatch =
      primaryRequested === primaryPressed &&
      parts.includes('shift') === event.shiftKey &&
      parts.includes('alt') === event.altKey;

    return keyMatch && modifiersMatch;
  } catch (error) {
    logger.error('Error checking shortcut:', error);
    return false;
  }
};

/**
 * Execute shortcut handler by key
 */
export const executeShortcut = (shortcutKey: string, context?: string): boolean => {
  try {
    const handlerKey = `${context || 'all'}:${shortcutKey}`;
    const handler = shortcutHandlers.get(handlerKey);

    if (!handler) {
      logger.debug(`No handler registered for shortcut: ${shortcutKey}`);
      return false;
    }

    // S78: a handler that THREW used to be reported as "no handler registered",
    // and the caller reads that return value to decide whether to
    // `preventDefault()`. So a broken Ctrl+S handler silently handed the chord
    // back to the browser's "Save page" dialog — the child pressed save and got a
    // file download. The shortcut WAS handled; the handler is what failed.
    try {
      handler();
    } catch (error) {
      logger.error(`Shortcut handler for "${shortcutKey}" threw:`, error);
      return true;
    }

    logger.debug(`Executed shortcut: ${shortcutKey}`);
    return true;
  } catch (error) {
    logger.error('Error executing shortcut:', error);
    return false;
  }
};

/**
 * Options for {@link setupGlobalKeyboardListener}.
 *
 * #44 (2026-05-22): the editor needs to suppress shortcuts while the user is
 * typing in the chat textarea / Monaco editor, while still letting Escape
 * through (so it can close panels). It also must NOT preventDefault on keys it
 * has no handler for, so the code editor keeps its native Ctrl+Z/Ctrl+F/etc.
 * The base behavior (no options) is unchanged for any existing caller.
 */
export interface GlobalKeyboardListenerOptions {
  /**
   * Returns true when keyboard events should be ignored because the user is
   * typing into an editable field. Shortcuts marked `allowWhileTyping`
   * (currently only Escape, via `id === 'close-modal'`) still fire.
   */
  shouldIgnore?: (event: KeyboardEvent) => boolean;
  /**
   * S78: the user's rebindings, as `{ [shortcutId]: 'Ctrl+Alt+S' }`.
   *
   * The listener used to test `shortcut.key` from the static `SHORTCUTS` array
   * and took no uid, so it could not see an override at all. The whole customize
   * feature was write-only: a child rebound save to Ctrl+Alt+S, the document was
   * written, and Ctrl+Alt+S did nothing while Ctrl+S kept saving.
   *
   * Load them with `getShortcutOverrides(uid)`.
   */
  overrides?: Record<string, string>;
}

// Shortcut ids that must fire even while the user is typing in an input.
// - `close-modal` (Escape): closes panels regardless of focus.
// - `save-project` (Ctrl/Cmd+S): a deliberate command chord; users expect it
//   to save even from the chat box or code editor, and it would otherwise
//   trigger the browser's "Save page" dialog.
// - `send-message` (Ctrl/Cmd+Enter): a command chord meant to send from the
//   chat textarea; it is not normal text entry so suppressing it is wrong.
const ALLOW_WHILE_TYPING = new Set<string>(['close-modal', 'save-project', 'send-message']);

/**
 * Setup global keyboard event listener
 */
export const setupGlobalKeyboardListener = (
  context?: string,
  options?: GlobalKeyboardListenerOptions
): (() => void) => {
  const handleKeyDown = (event: KeyboardEvent) => {
    try {
      const ignoring = options?.shouldIgnore?.(event) ?? false;

      // Check each shortcut
      for (const shortcut of SHORTCUTS) {
        // S78: an override REPLACES the default chord rather than adding to it.
        // Leaving the default live would mean a child who rebound a key still had
        // the old one firing, which is the opposite of what rebinding means.
        const chord = options?.overrides?.[shortcut.id] || shortcut.key;
        if (isShortcutPressed(event, chord)) {
          // While typing, only fire the allow-listed shortcuts (Escape).
          if (ignoring && !ALLOW_WHILE_TYPING.has(shortcut.id)) {
            continue;
          }
          // Only consume the event when a handler is actually registered.
          // This keeps native key handling (Monaco's Ctrl+Z/Ctrl+F, etc.)
          // intact for any shortcut the editor chose not to bind.
          if (executeShortcut(shortcut.id, context)) {
            event.preventDefault();
            return;
          }
        }
      }
    } catch (error) {
      logger.error('Error in global keyboard listener:', error);
    }
  };

  document.addEventListener('keydown', handleKeyDown);

  // Return unsubscribe function
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
};

/**
 * S78: the rebindings the SETTINGS SCREEN actually writes, as chords.
 *
 * There were three disconnected pieces of one feature:
 *
 *   1. `ShortcutSettings.tsx` reads and writes `userPreferences/{uid}.shortcuts`
 *      as `[{ id, keys: string[] }]` — the only path anything in the product
 *      writes, and the only one with a rule that has always existed.
 *   2. `saveCustomShortcut` below writes `users/{uid}/preferences/shortcuts` as
 *      `[{ shortcutId, customKey }]`. It has no callers, and until S77 that path
 *      had no matching rule at all, so the write was denied anyway.
 *   3. `setupGlobalKeyboardListener` read NEITHER, so nothing a child rebound
 *      ever fired.
 *
 * This reads (1) — the data that exists — and converts it to the `'Ctrl+Alt+S'`
 * chord form `isShortcutPressed` parses, so the listener can honour it. Reading
 * it is not optional for correctness anywhere, so a failed read yields no
 * overrides and the DEFAULTS keep working, which is the safe direction: a child
 * loses their rebinding for this session rather than losing their shortcuts.
 */
export const getShortcutOverrides = async (
  uid: string
): Promise<Record<string, string>> => {
  if (!uid) return {};
  try {
    const prefDoc = await getDoc(doc(db.value, 'userPreferences', uid));
    if (!prefDoc.exists()) return {};

    const stored = prefDoc.data().shortcuts;
    if (!Array.isArray(stored)) return {};

    const overrides: Record<string, string> = {};
    for (const entry of stored as Array<{ id?: unknown; keys?: unknown }>) {
      if (typeof entry?.id !== 'string' || !Array.isArray(entry.keys)) continue;
      const keys = entry.keys.filter((k): k is string => typeof k === 'string' && k !== '');
      if (keys.length === 0) continue;

      // Only accept an override for a shortcut that exists and permits one — a
      // stored entry is a claim, not a guarantee (rule 8d), and an override on a
      // non-customizable shortcut would be a rebinding nobody authorized.
      const shortcut = SHORTCUTS.find((sc) => sc.id === entry.id);
      if (!shortcut || !shortcut.canCustomize) continue;

      overrides[entry.id] = keys.join('+');
    }
    return overrides;
  } catch (error) {
    logger.error('Could not read custom shortcut overrides:', error);
    return {};
  }
};

/**
 * Get user's custom shortcuts from Firestore
 */
export const getUserCustomShortcuts = async (uid: string): Promise<CustomShortcut[]> => {
  try {
    logger.debug(`Fetching custom shortcuts for user ${uid}`);

    const shortcutsDoc = await getDoc(doc(db.value, 'users', uid, 'preferences', 'shortcuts'));

    if (!shortcutsDoc.exists()) {
      logger.debug('No custom shortcuts found');
      return [];
    }

    const customShortcuts = shortcutsDoc.data().shortcuts || [];
    logger.debug(`Retrieved ${customShortcuts.length} custom shortcuts`);

    return customShortcuts;
  } catch (error) {
    // S78: this returned `[]` — the same value as "this child has customized
    // nothing" — so a refused read handed back the defaults with no error and no
    // way to tell the difference. The settings screen would then show the default
    // chords as though the child had never rebound anything, and saving from that
    // screen would overwrite the overrides they could not see.
    //
    // (`getShortcutOverrides`, which the LISTENER uses, deliberately does return
    // `{}` on failure: losing a rebinding for a session is better than a keyboard
    // that does nothing. This is the settings-screen read, where being wrong is
    // destructive.)
    logger.error('Error getting custom shortcuts:', error);
    throw error;
  }
};

/**
 * Save a custom shortcut override
 */
export const saveCustomShortcut = async (
  uid: string,
  shortcutId: string,
  newKey: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    logger.debug(`Saving custom shortcut for ${shortcutId}`);

    // Validate the shortcut exists
    const shortcut = SHORTCUTS.find(s => s.id === shortcutId);
    if (!shortcut || !shortcut.canCustomize) {
      return { success: false, error: 'Shortcut cannot be customized' };
    }

    // Validate key format
    if (!isValidKeyFormat(newKey)) {
      return { success: false, error: 'Invalid key format' };
    }

    // #30 fix (2026-05-17): conflict detection. Reject the save if the
    // requested key collides with any other shortcut in the same context.
    // Without this guard, two actions can bind to the same key and only one
    // ever wins (whichever the handler map yields first).
    // S78: the context comparison used to let a rebinding onto Escape through.
    // `close-modal` is context 'all' and `save-project` is context 'editor', so
    // `s.context === shortcut.context` was false and they never compared — while
    // `setupGlobalKeyboardListener` walks EVERY shortcut regardless of context, so
    // in the editor both matched one Escape press and only the first in array order
    // won. A shortcut with no context, or with context 'all', is live everywhere
    // and therefore conflicts with everything.
    const isGlobalContext = (context?: string) => !context || context === 'all';
    const conflictingBuiltin = SHORTCUTS.find(
      (s) =>
        s.id !== shortcutId &&
        s.key.toLowerCase() === newKey.toLowerCase() &&
        (isGlobalContext(s.context) ||
          isGlobalContext(shortcut.context) ||
          s.context === shortcut.context)
    );
    if (conflictingBuiltin) {
      return {
        success: false,
        error: `Conflict: "${newKey}" is already used by "${conflictingBuiltin.id}"`,
      };
    }

    const shortcutsRef = doc(db.value, 'users', uid, 'preferences', 'shortcuts');

    // Get existing shortcuts
    const shortcutsDoc = await getDoc(shortcutsRef);
    const existing = shortcutsDoc.exists() ? shortcutsDoc.data().shortcuts || [] : [];

    // Also reject collisions with the user's other custom overrides.
    const conflictingCustom = (existing as CustomShortcut[]).find(
      (s) => s.shortcutId !== shortcutId && s.customKey?.toLowerCase() === newKey.toLowerCase()
    );
    if (conflictingCustom) {
      return {
        success: false,
        error: `Conflict: "${newKey}" is already used by another custom shortcut`,
      };
    }

    // Update or add custom shortcut
    const customShortcuts = existing.filter((s: CustomShortcut) => s.shortcutId !== shortcutId);
    customShortcuts.push({
      shortcutId,
      key: shortcut.key,
      customKey: newKey,
      isCustom: true,
      // FIXED 2026-09-10, found by running this service against real Firestore.
      // This was `serverTimestamp()`, and the SDK refuses that inside an ARRAY
      // element during argument validation, before any request is sent:
      // "serverTimestamp() is not currently supported inside arrays"
      // (@firebase/firestore, index.node.cjs.js:30366). The throw landed in the
      // catch below, so EVERY rebinding a child made returned
      // `{ success: false, error: 'Failed to save custom shortcut' }` — the
      // whole customize feature, refused by the client SDK. The in-memory test
      // double stored the sentinel as an ordinary value, so eleven tests in
      // `keyboardShortcutService.test.ts` asserted the successful save of a
      // write that could never happen. Reverting this line fails those eleven.
      //
      // A client clock is acceptable here where a server one is not: this is a
      // display/ordering stamp on the user's own preference document, not an
      // audit record or anything a rule or a Cloud Function reads.
      updatedAt: Timestamp.now()
    });

    // Save to Firestore
    await setDoc(shortcutsRef, { shortcuts: customShortcuts }, { merge: true });

    logger.debug(`Saved custom shortcut: ${shortcutId} = ${newKey}`);

    return { success: true };
  } catch (error) {
    logger.error('Error saving custom shortcut:', error);
    return { success: false, error: 'Failed to save custom shortcut' };
  }
};

/**
 * Reset shortcut to default
 */
export const resetShortcutToDefault = async (
  uid: string,
  shortcutId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    logger.debug(`Resetting shortcut ${shortcutId} to default`);

    const shortcutsRef = doc(db.value, 'users', uid, 'preferences', 'shortcuts');
    const shortcutsDoc = await getDoc(shortcutsRef);

    if (!shortcutsDoc.exists()) {
      return { success: true }; // No custom shortcuts to reset
    }

    const existing = shortcutsDoc.data().shortcuts || [];
    const updated = existing.filter((s: CustomShortcut) => s.shortcutId !== shortcutId);

    await setDoc(shortcutsRef, { shortcuts: updated }, { merge: true });

    logger.debug(`Reset shortcut to default: ${shortcutId}`);

    return { success: true };
  } catch (error) {
    logger.error('Error resetting shortcut:', error);
    return { success: false, error: 'Failed to reset shortcut' };
  }
};

/**
 * Reset all shortcuts to defaults
 */
export const resetAllShortcutsToDefaults = async (
  uid: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    logger.debug(`Resetting all shortcuts to defaults for user ${uid}`);

    const shortcutsRef = doc(db.value, 'users', uid, 'preferences', 'shortcuts');
    await setDoc(shortcutsRef, { shortcuts: [] }, { merge: true });

    logger.debug('All shortcuts reset to defaults');

    return { success: true };
  } catch (error) {
    logger.error('Error resetting all shortcuts:', error);
    return { success: false, error: 'Failed to reset shortcuts' };
  }
};

/**
 * Validate key format (e.g., "Ctrl+S", "Shift+Alt+D")
 */
function isValidKeyFormat(keyString: string): boolean {
  // S78: three holes, all of which produced a chord that is STORED and can never
  // fire — the worst outcome for a rebinding UI, because the child sees it saved.
  //
  //   * `'Ctrl+'` split to `['Ctrl','']`; the empty last part is not in the
  //     modifier list, so it passed. `isShortcutPressed` then finds no key part
  //     and returns false for every event.
  //   * `''` passed for the same reason.
  //   * the modifier list was matched case-SENSITIVELY while `isShortcutPressed`
  //     lowercases everything, so `'ctrl+s'` — the form a recorder that reads
  //     `event.key` naturally produces — was rejected as invalid even though it
  //     would have matched perfectly.
  const parts = keyString.split('+').map((p) => p.trim());
  if (parts.length === 0) return false;
  // An empty segment anywhere means a trailing/leading '+' or a '++'.
  if (parts.some((p) => p === '')) return false;

  const validModifiers = ['ctrl', 'shift', 'alt', 'cmd', 'meta'];
  const lastPart = parts[parts.length - 1].toLowerCase();

  // Last part must be a key, not a modifier.
  if (validModifiers.includes(lastPart)) return false;

  // Everything before it must be a modifier.
  for (let i = 0; i < parts.length - 1; i++) {
    if (!validModifiers.includes(parts[i].toLowerCase())) return false;
  }

  return true;
}

/**
 * Get effective shortcut key (custom or default)
 */
export const getEffectiveShortcut = (
  shortcutId: string,
  customShortcuts: CustomShortcut[]
): string => {
  const custom = customShortcuts.find(s => s.shortcutId === shortcutId);
  if (custom && custom.customKey) {
    return custom.customKey;
  }

  const defaultShortcut = SHORTCUTS.find(s => s.id === shortcutId);
  return defaultShortcut ? defaultShortcut.key : '';
};

/**
 * Export all shortcut functions
 */
export const keyboardShortcutService = {
  registerShortcut,
  unregisterShortcut,
  getShortcutsForContext,
  getAllShortcuts,
  isShortcutPressed,
  executeShortcut,
  setupGlobalKeyboardListener,
  getUserCustomShortcuts,
  saveCustomShortcut,
  resetShortcutToDefault,
  resetAllShortcutsToDefaults,
  getEffectiveShortcut
};
